import axios from 'axios';
import { useMemo } from 'react';
import { GetWebApiUrl } from '../Constants/utils';
import { useAuthorization, AuthorizationState } from '../Auth/AuthorizationProvider';
import { AdoWorkItemLinksResponse, CidsReingestRequest, ContentIngestionRequest, ContentPackageDetails, MisBusinessInfo, ErrorData, MarketplaceIngestRequest, MarketplacePackItemIngestRequest, MarketplacePricesEntry, MarketplaceSummaryData, MisPackageDetailsResponse, PackageDetails, ProductReleaseRequest, ProductReleaseResponse, PublishingTarget, SpmPackageLinksResponse, SubmissionType } from '../Models/AugerOffers/MarketplaceDetails';
import { uploadBrowserData } from './uploadBrowserData';
import { MarketplaceProductBrowserData } from '../Components/MarketplaceProductBrowserData';
import { ReleaseSaleRequestData } from '../Utils/SalesUtils';
import { WasmRepoCatalog, WasmBuildCatalog, WasmMergedCatalog, WasmBuildRecommendation } from '../Models/Wasm/WasmCatalog';
import { WasmApiPlatform } from '../Models/Wasm/WasmPlatform';
const API_URL = GetWebApiUrl();

export interface OrderBy {
    id: string;
    desc: boolean;
}

export interface AdminSettings {
    id?: string;
    _etag?: string;
    currentGameVersion2020: string;
    minimumCompatibilityVersion2024: string;
    bannerText: string;
    bannerVersion: number;
    isOffline: boolean;
    showAneida: boolean;
    aneidaApprovedPackages: string[];
    chatEditWindowMinutes: number;
    chatDeleteWindowMinutes: number;
}

export interface UserPreferences {
    notifyOnMention: boolean;
    notifyOnAssignedChannel: boolean;
    notifyOnQuotedReply: boolean;
    notifyOnUnmutedChannel: boolean;
    mutedChannelIds: string[];
}

export interface SaleEligiblePackage {
    packageName: string;
    releaseDate: string | null;
}


export function useRomaWebApi(authOverride?: AuthorizationState) {
    const contextAuth = useAuthorization();
    const auth = authOverride ?? contextAuth;

    return useMemo(() => ({

        getMarketplacePackages: async (): Promise<MarketplaceSummaryData[]> => {
            let pageNumber = 0;
            const pageSize = 1000;
            let results:MarketplaceSummaryData[] = [];
            const accessToken = await auth.getAccessToken();
            const options = {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            }

            while (true) {
                const response = await axios.get(`${API_URL}/marketplace/summary?pageNumber=${pageNumber}&pageSize=${pageSize}`, options);
                const data: MarketplaceSummaryData[] = response.data;

                results = results.concat(data);

                if (data.length === 0) {
                    break;
                }

                pageNumber++;
            }

            return results;
        },

        getMarketplaceSummary: (pageNumber: number, pageSize: number): Promise<MarketplaceSummaryData[]> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/marketplace/summary?pageNumber=${pageNumber}&pageSize=${pageSize}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getMarketplaceProductData: (pageNumber: number, pageSize: number, sortBy?: OrderBy[], filterValue?: string, filterBy?: string): Promise<{ data: MarketplaceProductBrowserData[], pageCount: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/marketplace/productdata?pageNumber=${pageNumber}&pageSize=${pageSize}&sortBy=${sortBy ? sortBy.map(o => `${o.id} ${o.desc ? 'desc' : 'asc'}`).join(',') : ''}${filterValue ? `&filterValue=${filterValue}${filterBy ? `&filterBy=${filterBy}` : ''}` : ''}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getMarketplacePackageDetails: (packageName: string, submissionId?: string): Promise<PackageDetails<MisPackageDetailsResponse>> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/marketplace/details/${packageName}${submissionId ? "?submissionId=" + submissionId : ""}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        filterMarketplacePackage: (packageName: string, platform: PublishingTarget): Promise<MarketplaceSummaryData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/filter/${packageName}/${platform}`, null, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        unfilterMarketplacePackage: (packageName: string, platform: PublishingTarget): Promise<MarketplaceSummaryData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/unfilter/${packageName}/${platform}`, null, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getMarketplacePackageSummary: (packageName: string): Promise<MarketplaceSummaryData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/marketplace/summary/${packageName}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getBulkMarketplaceSummaries: (packageNames: string[]): Promise<{ [packageName: string]: MarketplaceSummaryData }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/summary/bulk`, { packageNames }, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        // Slim projection of getBulkMarketplaceSummaries that returns only release+prerelease
        // catalog prices. Used by the bulk-edit / bulk-approve sale flows where the full
        // MarketplaceSummaryData (25+ fields per version) was producing multi-MB payloads
        // and 10-15s waits for sales with thousands of packages.
        getBulkMarketplacePrices: (packageNames: string[]): Promise<{ [packageName: string]: MarketplacePricesEntry }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/prices/bulk`, { packageNames }, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        deleteMarketplacePackage: (packageName: string, publisherId: string, developerId: string): Promise<void> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/delete/${packageName}/${publisherId}/${developerId}`, undefined, options).then(() => {
                            resolve();
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getContentPackages: (baseContent: boolean = false): Promise<ContentPackageDetails[]> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/content/summary?baseContent=${baseContent}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getContentPackageSummary: (packageName: string): Promise<ContentPackageDetails> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/content/summary/${packageName}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getContentPackageDetails: (packageName: string): Promise<ContentPackageDetails> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/content/details/${packageName}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        deleteContentPackage: (packageName: string, publishingTarget: PublishingTarget, publisherId: string, developerId: string): Promise<void> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }
                        const data = {
                            packageName: packageName,
                            publisherId: publisherId,
                            developerId: developerId,
                            publishingTarget: publishingTarget
                        }

                        axios.post(`${API_URL}/content/clean`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        uploadMediaAsset: (file: File | Blob, fileName: string, id:string, packageName:string, developerId: string, publisherId: string, progressCallback?: any) => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        /// alert("UploadFile " + fileName + "(1) size: " + file.size + " name(2): " + file.name )
                        if (file.size < 1) {
                            reject(`file ${fileName} cannot be uploaded (file size: ${file.size})`);
                            return;
                        }
                        // if (file.size > MAX_UPLOAD_SIZE) {
                        //     reject("File " + file.name + " is larger than " + MAX_UPLOAD_SIZE / (1024 * 1024) + "MB!");
                        // }

                        const data = {
                            developerId: developerId,
                            filename: fileName,
                            packageName: packageName,
                            publisherId: publisherId,
                            submissionId: id
                        }

                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/uploadUrls`, data, options).then(response => {
                            const url = response.data.url;

                            uploadBrowserData(url, file, function (ev: any) {
                                var progress = (ev.loadedBytes * 100) / file.size;
                                if (progressCallback) {
                                    progressCallback(progress);
                                }
                            }, function () {
                                resolve(url);
                            }, function (error: any) {
                                reject(error);
                            });
                        }).catch((error: string) => {
                            console.log("error:", error);
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        ingestMarketplaceProductCarryOverRental: (packageName: string, submissionType: SubmissionType, data?: MisBusinessInfo): Promise<MarketplaceSummaryData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/carryover/marketplace/rental/${packageName}/${submissionType}`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        ingestMarketplaceProduct: (data: MarketplaceIngestRequest): Promise<MarketplaceSummaryData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/ingest`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        ingestMarketplacePackItem: (data: MarketplacePackItemIngestRequest): Promise<MarketplaceSummaryData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/marketplace/ingest/packitem`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        ingestContentPackage: (data: ContentIngestionRequest) => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.put(`${API_URL}/content/ingest/start`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        reingestContentPackage: (data: CidsReingestRequest) => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.put(`${API_URL}/content/reingest`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        transferToPrerelease: (data: ProductReleaseRequest, ignoreWorkItem?: boolean): Promise<ProductReleaseResponse> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.put(`${API_URL}/publish/prerelease${ignoreWorkItem ? "?ignoreWorkItem=true": ""}`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        transferToPrereleaseForResubmission: (data: ProductReleaseRequest): Promise<ProductReleaseResponse> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.put(`${API_URL}/publish/resubmission`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        transferToRelease: (data: ProductReleaseRequest): Promise<ProductReleaseResponse> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.put(`${API_URL}/publish/release`, data, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        // Recovery hatch for a package that transferred to Prerelease but never got an ADO work item
        // because the service bus message failed to send. Performs no transfer; safe to call repeatedly.
        requeueWorkItem: (packageName: string): Promise<ProductReleaseResponse> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.post(`${API_URL}/publish/requeue-workitem/${encodeURIComponent(packageName)}`, null, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getPublisherIds: () => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/user/publishers`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getDeveloperIds: (publisherId: string) => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/user/publishers/${publisherId}/developers`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getMarketplaceError: (packageName: string, submissionId?: string): Promise<ErrorData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/marketplace/error/${packageName}/${submissionId ?? ""}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        // Internal-only. Returns the ADO work items for a package along with server-composed browser
        // links, so the portal never has to hardcode the ADO organization or project.
        getAdoWorkItemLinks: (packageName: string): Promise<AdoWorkItemLinksResponse> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/publish/${encodeURIComponent(packageName)}/ado-work-items`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        // Internal-only. Returns the package's SPM links, composed server side so the environment
        // specific SPM host stays in API configuration rather than the client bundle.
        getSpmPackageLinks: (packageName: string): Promise<SpmPackageLinksResponse> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/publish/${encodeURIComponent(packageName)}/spm-links`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getContentError: (packageName: string, submissionId: string, publishingTarget: PublishingTarget): Promise<ErrorData> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/content/error/${packageName}/${submissionId}/${publishingTarget}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getAllSales: (includePreviousSales?: boolean): Promise<any[]> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        const url = `${API_URL}/sales/all/sales${includePreviousSales !== undefined ? `?includePreviousSales=${includePreviousSales}` : ''}`;
                        axios.get(url, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getAllPackageSalesBySaleId: (saleId: string): Promise<any[]> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.get(`${API_URL}/sales/all/sale/packages/${saleId}`, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        upsertDraftSale: (saleRequest: any): Promise<any> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/upsert/draft`, saleRequest, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        upsertDraftSalePackages: (packagesRequest: any): Promise<{ data: any; status: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/add/packages/draft`, packagesRequest, options).then(response => {
                            resolve({ data: response.data, status: response.status });
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        addReleasedSalePackages: (packagesRequest: any): Promise<{ data: any; status: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/released/${encodeURIComponent(packagesRequest.saleId)}/packages`, packagesRequest, options).then(response => {
                            resolve({ data: response.data, status: response.status });
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        approveOrRejectDraftSalePackages: (saleId: string, status: string, packages: any, rejectionNote?: string): Promise<any> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        const url = rejectionNote 
                            ? `${API_URL}/sales/status/packages/${saleId}/${status}?rejectionNote=${encodeURIComponent(rejectionNote)}`
                            : `${API_URL}/sales/status/packages/${saleId}/${status}`;
                        
                        axios.post(url, packages, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        approveOrRejectDraftSale: (saleId: string, status: string): Promise<{ data: any; status: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/status/draft/${saleId}/${status}`, {}, options).then(response => {
                            resolve({ data: response.data, status: response.status });
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        retryFailedSalePackages: (saleId: string): Promise<{ data: any; status: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/retry/${saleId}/failed`, {}, options).then(response => {
                            resolve({ data: response.data, status: response.status });
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        bulkApproveOrRejectDraftSalePackages: (saleId: string, packageStatuses: { [packageName: string]: { [platform: string]: { Status: string, RejectionNote?: string } } }): Promise<{ data: any; status: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/status/packages/bulk/${saleId}`, packageStatuses, options).then(response => {
                            resolve({ data: response.data, status: response.status });
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        getSaleEligiblePackages: (saleId?: string): Promise<SaleEligiblePackage[]> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        const url = saleId 
                            ? `${API_URL}/sales/eligible/packages?saleId=${encodeURIComponent(saleId)}`
                            : `${API_URL}/sales/eligible/packages`;

                        axios.get(url, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        deleteDraftSale: (saleId: string): Promise<void> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        axios.delete(`${API_URL}/sales/draft/${encodeURIComponent(saleId)}`, options).then(() => {
                            resolve();
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        deleteDraftSalePackage: (saleId: string, packageName: string, platform: string): Promise<any> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        const url = `${API_URL}/sales/draft/${encodeURIComponent(saleId)}/packages?packageName=${encodeURIComponent(packageName)}&platform=${encodeURIComponent(platform)}`;
                        axios.delete(url, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        deprecateReleasedSalePackage: (saleId: string, packageName: string, platform: string): Promise<any> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`
                            }
                        }

                        const url = `${API_URL}/sales/released/${encodeURIComponent(saleId)}/packages?packageName=${encodeURIComponent(packageName)}&platform=${encodeURIComponent(platform)}`;
                        axios.delete(url, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        updateApprovedSalePackages: (packagesRequest: any): Promise<{ data: any; status: number }> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/update/approved/${encodeURIComponent(packagesRequest.saleId)}/packages`, packagesRequest, options).then(response => {
                            resolve({ data: response.data, status: response.status });
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        updateApprovedSaleRanges: (saleId: string, startDate: string, endDate: string): Promise<any> => {
            return new Promise(
                (resolve, reject) => {
                    auth.getAccessToken().then((accessToken: string) => {
                        const options = {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                                'Content-Type': 'application/json'
                            }
                        }

                        axios.post(`${API_URL}/sales/update/approved/${encodeURIComponent(saleId)}/ranges`, { startDate, endDate }, options).then(response => {
                            resolve(response.data);
                        }).catch(error => {
                            reject(error);
                        });
                    }).catch((error: string) => {
                        reject(error);
                    });
                }
            );
        },

        upsertReleaseSale: (request: ReleaseSaleRequestData): Promise<any> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    };
                    axios.post(`${API_URL}/sales/release-sale/upsert`, request, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        getAdminSettings: (): Promise<AdminSettings> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.get(`${API_URL}/settings`, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        updateAdminSettings: (settings: AdminSettings): Promise<AdminSettings> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.put(`${API_URL}/settings`, settings, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        addAneidaApprovedPackage: (packageName: string): Promise<AdminSettings> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.put(`${API_URL}/settings/aneida-approved-packages/${encodeURIComponent(packageName)}`, null, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        getUserPreferences: (): Promise<UserPreferences> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.get(`${API_URL}/user/preferences`, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        updateUserPreferences: (preferences: UserPreferences): Promise<UserPreferences> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.put(`${API_URL}/user/preferences`, preferences, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        getWasmRepoCatalog: (platform: WasmApiPlatform): Promise<WasmRepoCatalog> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.get(`${API_URL}/wasm/${platform}/repoCatalog`, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        getWasmBuildCatalog: (platform: WasmApiPlatform): Promise<WasmBuildCatalog> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.get(`${API_URL}/wasm/${platform}/buildCatalog`, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        getWasmMergedView: (platform: WasmApiPlatform): Promise<WasmMergedCatalog> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.get(`${API_URL}/wasm/${platform}/mergedView`, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

        getWasmBuildRecommendation: (platform: WasmApiPlatform): Promise<WasmBuildRecommendation> => {
            return new Promise((resolve, reject) => {
                auth.getAccessToken().then((accessToken: string) => {
                    const options = { headers: { Authorization: `Bearer ${accessToken}` } };
                    axios.get(`${API_URL}/wasm/${platform}/buildRecommendation`, options)
                        .then(response => resolve(response.data))
                        .catch(error => reject(error));
                }).catch((error: string) => reject(error));
            });
        },

    }), [auth]);
}

